<template>
  <div class="contact-list-scroll">
    <!-- Search bar -->
    <div class="contact-list-scroll__search">
      <div class="contact-list-scroll__search-inner">
        <i class="fa-light fa-magnifying-glass contact-list-scroll__search-icon" />
        <input
          v-model="searchTerm"
          type="text"
          class="contact-list-scroll__search-input"
          :placeholder="t('flows.operatorPanel.search')"
          @input="onSearchInput"
        />
        <i
          v-if="searchTerm"
          class="fa-light fa-xmark contact-list-scroll__search-clear"
          @click="clearSearch"
        />
      </div>
    </div>

    <!-- Selected contact banner -->
    <div
      v-if="modelValue && selectedContactInfo"
      class="contact-list-scroll__selected-banner"
    >
      <div class="contact-list-scroll__selected-banner-info">
        <i class="fa-light fa-user-check contact-list-scroll__selected-banner-icon" />
        <div class="contact-list-scroll__selected-banner-text">
          <span class="contact-list-scroll__selected-banner-name">
            {{ selectedContactInfo.name || t('myLists.manage.customers.noName') }}
          </span>
          <span class="contact-list-scroll__selected-banner-email">
            {{ selectedContactInfo.email || '-' }}
          </span>
        </div>
      </div>
      <button
        class="contact-list-scroll__selected-banner-clear"
        :title="t('templateBuilder.modals.sendTestV4.deselectContact')"
        @click="deselectContact"
      >
        <i class="fa-light fa-xmark" />
      </button>
    </div>

    <!-- Segment conditions section -->
    <div
      v-if="segmentInfo.hasSegments && !allContactsOnly"
      class="contact-list-scroll__segment-section"
    >
      <!-- Notice message -->
      <div class="contact-list-scroll__segment-notice">
        <i class="fa-light fa-lightbulb contact-list-scroll__segment-notice-icon" />
        <span class="contact-list-scroll__segment-notice-text">
          {{ t('templateBuilder.modals.sendTestV4.segmentNotice', segmentInfo.conditions.length) }}
        </span>
      </div>

      <!-- Conditions list with checkboxes -->
      <div class="contact-list-scroll__conditions">
        <label
          v-for="(condition, index) in segmentInfo.conditions"
          :key="condition.id"
          class="contact-list-scroll__condition"
          :class="{ 'contact-list-scroll__condition--checked': selectedConditionIds.includes(condition.id) }"
        >
          <span class="contact-list-scroll__condition-check">
            <i v-if="selectedConditionIds.includes(condition.id)" class="fa-solid fa-check" />
          </span>
          <input
            type="checkbox"
            :checked="selectedConditionIds.includes(condition.id)"
            class="contact-list-scroll__condition-checkbox"
            @change="toggleCondition(condition.id)"
          />
          <span class="contact-list-scroll__condition-label">
            {{ t('templateBuilder.modals.sendTestV4.conditionLabel', { index: index + 1 }) }}
          </span>
          <span
            class="contact-list-scroll__condition-info-wrapper"
            @click.prevent.stop
            @mouseenter.stop="showConditionPopover($event, condition)"
            @mouseleave="hideConditionPopover"
          >
            <i class="fa-light fa-circle-info contact-list-scroll__condition-info" />
          </span>
        </label>
      </div>

      <!-- Condition popover using OverlayPanel -->
      <OverlayPanel
        ref="conditionOverlayRef"
        class="contact-list-scroll__overlay-panel"
      >
        <div class="contact-list-scroll__popover-content">
          <SegmentationsPanel
            v-if="activePopover"
            :key="activePopover.id"
            :model-value="activePopover.config"
            size="small"
            :readonly="true"
          />
        </div>
      </OverlayPanel>

      <!-- Filter tabs -->
      <div class="contact-list-scroll__tabs">
        <button
          class="contact-list-scroll__tab"
          :class="{ 'contact-list-scroll__tab--active': activeTab === 'matching' }"
          @click="switchTab('matching')"
        >
          {{ t('templateBuilder.modals.sendTestV4.matchingContacts') }}
          <span class="contact-list-scroll__tab-count">{{ matchingTotal }}</span>
        </button>
        <button
          class="contact-list-scroll__tab"
          :class="{ 'contact-list-scroll__tab--active': activeTab === 'others' }"
          @click="switchTab('others')"
        >
          {{ t('templateBuilder.modals.sendTestV4.otherContacts') }}
          <span class="contact-list-scroll__tab-count">{{ nonMatchingTotal }}</span>
        </button>
      </div>
    </div>

    <!-- Contact list -->
    <div
      ref="scrollContainerRef"
      class="contact-list-scroll__list"
    >
      <!-- Loading overlay -->
      <div
        v-if="loading && !isInitialLoading"
        class="contact-list-scroll__overlay"
      >
        <i class="fa-light fa-spinner-third fa-spin contact-list-scroll__overlay-spinner" />
      </div>
      <!-- Initial loading -->
      <div
        v-if="isInitialLoading"
        class="contact-list-scroll__loading"
      >
        <div
          v-for="i in 6"
          :key="i"
          class="contact-list-scroll__skeleton"
        >
          <div class="contact-list-scroll__skeleton-radio" />
          <div class="contact-list-scroll__skeleton-content">
            <div class="contact-list-scroll__skeleton-name" />
            <div class="contact-list-scroll__skeleton-email" />
          </div>
        </div>
      </div>

      <!-- ===== SEGMENT MODE ===== -->
      <template v-if="segmentInfo.hasSegments && !allContactsOnly && !isInitialLoading">
        <!-- Matching tab content -->
        <template v-if="activeTab === 'matching'">
          <div
            v-for="contact in matchingContacts"
            :key="'m-' + contact.id_contact"
            class="contact-list-scroll__row contact-list-scroll__row--matching"
            :class="{ 'contact-list-scroll__row--selected': modelValue === Number(contact.id_contact) }"
            @click="selectContact(contact)"
          >
            <RadioButton
              :model-value="modelValue"
              :value="Number(contact.id_contact)"
              class="contact-list-scroll__radio"
              @update:model-value="selectContact(contact)"
            />
            <div class="contact-list-scroll__info">
              <div class="contact-list-scroll__name-row">
                <span
                  v-if="formatFullName(contact.first_name, contact.last_name)"
                  class="contact-list-scroll__name"
                >
                  {{ formatFullName(contact.first_name, contact.last_name) }}
                </span>
                <span
                  v-else
                  class="contact-list-scroll__name contact-list-scroll__name--empty"
                >
                  {{ t('myLists.manage.customers.noName') }}
                </span>
              </div>
              <div class="contact-list-scroll__email">
                {{ contact.email || '-' }}
              </div>
            </div>
            <div class="contact-list-scroll__match-badges">
              <span
                v-for="cId in (contact.matched_conditions || [])"
                :key="cId"
                class="contact-list-scroll__match-badge"
                :title="t('templateBuilder.modals.sendTestV4.conditionLabel', { index: cId + 1 })"
              >C{{ cId + 1 }}</span>
            </div>
          </div>
        </template>

        <!-- Others tab content -->
        <template v-if="activeTab === 'others'">
          <div
            v-for="contact in nonMatchingContacts"
            :key="'o-' + contact.id_contact"
            class="contact-list-scroll__row"
            :class="{ 'contact-list-scroll__row--selected': modelValue === Number(contact.id_contact) }"
            @click="selectContact(contact)"
          >
            <RadioButton
              :model-value="modelValue"
              :value="Number(contact.id_contact)"
              class="contact-list-scroll__radio"
              @update:model-value="selectContact(contact)"
            />
            <div class="contact-list-scroll__info">
              <div class="contact-list-scroll__name-row">
                <span
                  v-if="formatFullName(contact.first_name, contact.last_name)"
                  class="contact-list-scroll__name"
                >
                  {{ formatFullName(contact.first_name, contact.last_name) }}
                </span>
                <span
                  v-else
                  class="contact-list-scroll__name contact-list-scroll__name--empty"
                >
                  {{ t('myLists.manage.customers.noName') }}
                </span>
              </div>
              <div class="contact-list-scroll__email">
                {{ contact.email || '-' }}
              </div>
            </div>
          </div>
        </template>
      </template>

      <!-- ===== REGULAR MODE (no segments) ===== -->
      <template v-if="(!segmentInfo.hasSegments || allContactsOnly) && !isInitialLoading">
        <div
          v-for="contact in contacts"
          :key="contact.id_contact"
          class="contact-list-scroll__row"
          :class="{ 'contact-list-scroll__row--selected': modelValue === Number(contact.id_contact) }"
          @click="selectContact(contact)"
        >
          <RadioButton
            :model-value="modelValue"
            :value="Number(contact.id_contact)"
            class="contact-list-scroll__radio"
            @update:model-value="selectContact(contact)"
          />
          <div class="contact-list-scroll__info">
            <div class="contact-list-scroll__name-row">
              <span
                v-if="formatFullName(contact.first_name, contact.last_name)"
                class="contact-list-scroll__name"
              >
                {{ formatFullName(contact.first_name, contact.last_name) }}
              </span>
              <span
                v-else
                class="contact-list-scroll__name contact-list-scroll__name--empty"
              >
                {{ t('myLists.manage.customers.noName') }}
              </span>
            </div>
            <div class="contact-list-scroll__email">
              {{ contact.email || '-' }}
            </div>
          </div>
        </div>
      </template>

      <!-- Empty state -->
      <div
        v-if="initialized && !loading && isEmpty"
        class="contact-list-scroll__empty"
      >
        <i class="fa-light fa-address-book contact-list-scroll__empty-icon" />
        <span>{{ t('templateBuilder.modals.sendTestV4.noContacts') }}</span>
      </div>

      <!-- Sentinel for infinite scroll -->
      <div
        ref="sentinelRef"
        class="contact-list-scroll__sentinel"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  reactive,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import RadioButton from 'primevue/radiobutton';
import OverlayPanel from 'primevue/overlaypanel';
import { useI18n } from 'vue-i18n';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { UserState } from '@/composables/User';
import SegmentationsPanel from '@/components/segmentations/SegmentationsPanel.vue';
import type { StandardQueryListing, SpmApiFilter } from '@/components/table-rest-api/types';

const BATCH_SIZE = 20;
const DEBOUNCE_MS = 300;

interface ContactRow {
  id_contact: number | string;
  first_name?: string;
  last_name?: string;
  email?: string;
  matched_conditions?: number[];
}

interface SegmentCondition {
  id: number;
  config: any;
}

interface SegmentInfo {
  hasSegments: boolean;
  conditions: SegmentCondition[];
  matchingCount: number;
}

export default defineComponent({
  name: 'ContactListScroll',

  components: {
    RadioButton,
    OverlayPanel,
    SegmentationsPanel,
  },

  props: {
    modelValue: {
      type: Number as any,
      default: undefined,
    },
    templateId: {
      type: Number,
      default: undefined,
    },
    initialContactInfo: {
      type: Object as any,
      default: null,
    },
    rawHtml: {
      type: String,
      default: undefined,
    },
    allContactsOnly: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'update:contact-info', 'update:segment-info'],

  setup(props, { emit }) {
    const allContactsOnly = computed(() => props.allContactsOnly);

    const { t } = useI18n();

    // ===== Segment state =====
    const segmentInfo = reactive<SegmentInfo>({
      hasSegments: false,
      conditions: [],
      matchingCount: 0,
    });

    // ===== Selected conditions state (all selected by default) =====
    const selectedConditionIds = ref<number[]>([]);

    // ===== Popover state =====
    const activePopover = ref<SegmentCondition | null>(null);
    const conditionOverlayRef = ref();

    // ===== Matching contacts state =====
    const matchingContacts = ref<ContactRow[]>([]);
    const matchingOffset = ref(0);
    const matchingHasMore = ref(true);
    const matchingTotal = ref(0);

    // ===== Non-matching contacts state =====
    const nonMatchingContacts = ref<ContactRow[]>([]);
    const nonMatchingOffset = ref(0);
    const nonMatchingHasMore = ref(true);
    const nonMatchingTotal = ref(0);

    // ===== Regular (no-segment) contacts state =====
    const contacts = ref<ContactRow[]>([]);
    const offset = ref(0);
    const hasMore = ref(true);

    // ===== Tab filter state =====
    const activeTab = ref<'matching' | 'others'>('matching');

    // ===== Shared state =====
    const initialized = ref(false);
    const loading = ref(false);
    const searchTerm = ref('');
    const scrollContainerRef = ref<HTMLElement | null>(null);
    const sentinelRef = ref<HTMLElement | null>(null);
    let observer: IntersectionObserver | null = null;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const isInitialLoading = computed(() => {
      // Show skeleton until initialized or while loading with no data
      if (!initialized.value) return true;
      if (segmentInfo.hasSegments && !allContactsOnly.value) {
        return loading.value && matchingContacts.value.length === 0 && nonMatchingContacts.value.length === 0;
      }
      return loading.value && contacts.value.length === 0;
    });

    const isEmpty = computed(() => {
      if (segmentInfo.hasSegments && !allContactsOnly.value) {
        if (activeTab.value === 'matching') return matchingContacts.value.length === 0;
        return nonMatchingContacts.value.length === 0;
      }
      return contacts.value.length === 0;
    });

    // ===== Helpers =====
    const cleanStr = (str: string | null | undefined): string => {
      if (!str || str === 'null' || str === 'undefined') return '';
      return str;
    };

    const formatFullName = (firstName?: string, lastName?: string): string => {
      const first = cleanStr(firstName);
      const last = cleanStr(lastName);
      if (!first && !last) return '';
      return `${first} ${last}`.trim();
    };

    // ===== Segment info fetch =====
    const fetchSegmentInfo = async () => {
      if (!props.templateId) {
        emit('update:segment-info', {
          hasSegments: false,
          conditions: [],
          matchingCount: 0,
        });
        return;
      }
      const idShop = UserState.activeShop?.id ?? 0;
      if (!idShop) {
        emit('update:segment-info', {
          hasSegments: false,
          conditions: [],
          matchingCount: 0,
        });
        return;
      }

      try {
        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/template/segment-info',
          {},
          '',
          { idShop, idTemplate: props.templateId, rawHtml: props.rawHtml || undefined },
          true,
        );

        if (result?.success) {
          segmentInfo.hasSegments = result.hasSegments;
          segmentInfo.conditions = result.conditions || [];
          segmentInfo.matchingCount = result.matchingCount;
          // Select all conditions by default
          selectedConditionIds.value = segmentInfo.conditions.map((c: SegmentCondition) => c.id);
          emit('update:segment-info', {
            hasSegments: segmentInfo.hasSegments,
            conditions: segmentInfo.conditions,
            matchingCount: segmentInfo.matchingCount,
          });
        }
      } catch (err) {
        console.error('ContactListScroll: segment-info error', err);
        emit('update:segment-info', {
          hasSegments: false,
          conditions: [],
          matchingCount: 0,
        });
      }
    };

    // ===== Segment contacts fetch =====
    const fetchSegmentContacts = async (mode: 'matching' | 'non_matching', reset = false) => {
      if (loading.value) return;
      const isMatching = mode === 'matching';
      const currentHasMore = isMatching ? matchingHasMore : nonMatchingHasMore;
      if (!reset && !currentHasMore.value) return;

      loading.value = true;

      if (reset) {
        if (isMatching) {
          matchingOffset.value = 0;
          matchingHasMore.value = true;
        } else {
          nonMatchingOffset.value = 0;
          nonMatchingHasMore.value = true;
        }
      }

      const idShop = UserState.activeShop?.id ?? 0;
      const currentOffset = isMatching ? matchingOffset.value : nonMatchingOffset.value;

      try {
        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/template/contacts-by-segment',
          {},
          '',
          {
            idShop,
            idTemplate: props.templateId,
            mode,
            offset: currentOffset,
            limit: BATCH_SIZE,
            search: searchTerm.value.trim() || undefined,
            selectedConditionIds: selectedConditionIds.value,
            rawHtml: props.rawHtml || undefined,
          },
          true,
        );

        if (result?.success && result.data) {
          const newContacts: ContactRow[] = result.data;
          const total = result.total ?? 0;

          if (isMatching) {
            matchingContacts.value = reset ? newContacts : [...matchingContacts.value, ...newContacts];
            matchingTotal.value = total;
            matchingOffset.value = matchingContacts.value.length;
            matchingHasMore.value = matchingContacts.value.length < total;
          } else {
            nonMatchingContacts.value = reset ? newContacts : [...nonMatchingContacts.value, ...newContacts];
            nonMatchingTotal.value = total;
            nonMatchingOffset.value = nonMatchingContacts.value.length;
            nonMatchingHasMore.value = nonMatchingContacts.value.length < total;
          }
        } else {
          if (reset) {
            if (isMatching) matchingContacts.value = [];
            else nonMatchingContacts.value = [];
          }
          if (isMatching) matchingHasMore.value = false;
          else nonMatchingHasMore.value = false;
        }
      } catch (error) {
        console.error(`ContactListScroll: fetch ${mode} error`, error);
        if (reset) {
          if (isMatching) matchingContacts.value = [];
          else nonMatchingContacts.value = [];
        }
        if (isMatching) matchingHasMore.value = false;
        else nonMatchingHasMore.value = false;
      } finally {
        loading.value = false;
      }
    };

    // ===== Regular contacts fetch (no segments) =====
    const buildOrFilters = (): SpmApiFilter[] => {
      const term = searchTerm.value.trim();
      if (!term) return [];

      return [
        { field: 'first_name', operator: 'CONTAINS', values: term },
        { field: 'last_name', operator: 'CONTAINS', values: term },
        { field: 'email', operator: 'CONTAINS', values: term },
      ];
    };

    const fetchRegularContacts = async (reset = false) => {
      if (loading.value) return;
      if (!reset && !hasMore.value) return;

      loading.value = true;

      if (reset) {
        offset.value = 0;
        hasMore.value = true;
      }

      const query: StandardQueryListing = {
        offset: offset.value,
        limit: BATCH_SIZE,
        fields: 'id_contact,first_name,last_name,email',
      };

      const orFilters = buildOrFilters();
      if (orFilters.length > 0) {
        query.orFilters = orFilters;
      }

      try {
        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          '/contacts/all',
          {},
          '',
          undefined,
          true,
          query,
        );

        if (response?.data) {
          const newContacts: ContactRow[] = response.data;
          contacts.value = reset ? newContacts : [...contacts.value, ...newContacts];
          const total = response.meta?.pagination?.total_items ?? 0;
          hasMore.value = contacts.value.length < total;
          offset.value = contacts.value.length;
        } else {
          if (reset) contacts.value = [];
          hasMore.value = false;
        }
      } catch (error) {
        console.error('ContactListScroll: fetch error', error);
        if (reset) contacts.value = [];
        hasMore.value = false;
      } finally {
        loading.value = false;
      }
    };

    // ===== Tab switch =====
    const switchTab = async (tab: 'matching' | 'others') => {
      if (activeTab.value === tab) return;
      activeTab.value = tab;

      // Load data for the tab if not yet loaded
      if (tab === 'others' && nonMatchingContacts.value.length === 0 && nonMatchingHasMore.value) {
        await fetchSegmentContacts('non_matching', true);
      }
    };

    // ===== Unified fetch dispatcher =====
    const fetchNext = async () => {
      if (!initialized.value) return;
      if (segmentInfo.hasSegments && !allContactsOnly.value) {
        if (activeTab.value === 'matching' && matchingHasMore.value) {
          await fetchSegmentContacts('matching');
        } else if (activeTab.value === 'others' && nonMatchingHasMore.value) {
          await fetchSegmentContacts('non_matching');
        }
      } else {
        await fetchRegularContacts();
      }
    };

    const resetAndFetch = async () => {
      // Reset loading state to allow new fetches
      loading.value = false;

      if (segmentInfo.hasSegments && !allContactsOnly.value) {
        matchingContacts.value = [];
        nonMatchingContacts.value = [];
        matchingOffset.value = 0;
        nonMatchingOffset.value = 0;
        matchingHasMore.value = true;
        nonMatchingHasMore.value = true;
        matchingTotal.value = 0;
        nonMatchingTotal.value = 0;

        // Load both tabs so counts are available immediately
        await fetchSegmentContacts('matching', true);
        await fetchSegmentContacts('non_matching', true);
      } else {
        contacts.value = [];
        await fetchRegularContacts(true);
      }
    };

    // ===== Search =====
    const onSearchInput = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        resetAndFetch();
      }, DEBOUNCE_MS);
    };

    const clearSearch = () => {
      searchTerm.value = '';
      resetAndFetch();
    };

    // ===== Selection =====
    const selectedContactInfo = ref<{ name: string; email: string } | null>(props.initialContactInfo || null);

    // Sync when parent updates initialContactInfo after mount (e.g. restored from localStorage)
    watch(() => props.initialContactInfo, (newVal) => {
      if (newVal) {
        selectedContactInfo.value = newVal;
      }
    });

    const selectContact = (contact: ContactRow) => {
      const id = Number(contact.id_contact);
      const info = {
        name: formatFullName(contact.first_name, contact.last_name),
        email: contact.email || '',
      };
      selectedContactInfo.value = info;
      emit('update:contact-info', info);
      emit('update:modelValue', id);
    };

    const deselectContact = () => {
      selectedContactInfo.value = null;
      emit('update:contact-info', null);
      emit('update:modelValue', undefined);
    };

    // ===== Condition toggle =====
    const toggleCondition = (conditionId: number) => {
      const index = selectedConditionIds.value.indexOf(conditionId);
      if (index === -1) {
        selectedConditionIds.value = [...selectedConditionIds.value, conditionId];
      } else {
        selectedConditionIds.value = selectedConditionIds.value.filter(id => id !== conditionId);
      }
      // Refetch contacts with new selection
      resetAndFetch();
    };

    // ===== Popover functions =====
    let popoverHideTimeout: ReturnType<typeof setTimeout> | null = null;

    const showConditionPopover = (event: Event, condition: SegmentCondition) => {
      // Clear any pending hide timeout
      if (popoverHideTimeout) {
        clearTimeout(popoverHideTimeout);
        popoverHideTimeout = null;
      }
      activePopover.value = condition;
      conditionOverlayRef.value?.show(event);
    };

    const hideConditionPopover = () => {
      // Small delay to allow moving mouse to the popover
      popoverHideTimeout = setTimeout(() => {
        conditionOverlayRef.value?.hide();
        activePopover.value = null;
      }, 150);
    };

    const toggleConditionPopover = (event: Event, condition: SegmentCondition) => {
      activePopover.value = condition;
      conditionOverlayRef.value?.toggle(event);
    };

    // ===== Observer =====
    const setupObserver = () => {
      if (!sentinelRef.value) return;

      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry?.isIntersecting && !loading.value) {
            fetchNext();
          }
        },
        {
          root: scrollContainerRef.value,
          rootMargin: '100px',
          threshold: 0,
        },
      );

      observer.observe(sentinelRef.value);
    };

    // ===== Lifecycle =====
    onMounted(async () => {
      if (props.templateId) {
        await fetchSegmentInfo();
      }
      await resetAndFetch();
      initialized.value = true;
      nextTick(() => setupObserver());
    });

    onBeforeUnmount(() => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    });

    watch(sentinelRef, () => {
      if (observer) observer.disconnect();
      nextTick(() => setupObserver());
    });

    return {
      t,
      segmentInfo,
      selectedConditionIds,
      activePopover,
      conditionOverlayRef,
      matchingContacts,
      nonMatchingContacts,
      matchingHasMore,
      matchingTotal,
      nonMatchingTotal,
      contacts,
      loading,
      initialized,
      isInitialLoading,
      isEmpty,
      searchTerm,
      scrollContainerRef,
      sentinelRef,
      allContactsOnly,
      activeTab,
      formatFullName,
      onSearchInput,
      clearSearch,
      selectedContactInfo,
      selectContact,
      deselectContact,
      toggleCondition,
      showConditionPopover,
      hideConditionPopover,
      toggleConditionPopover,
      switchTab,
    };
  },
});
</script>

<style scoped lang="scss">
.contact-list-scroll {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: #fff;
}

// ---- Search bar ----
.contact-list-scroll__search {
  flex-shrink: 0;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.contact-list-scroll__search-inner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 0.5rem 0.875rem;
  transition: all 0.2s ease;

  &:focus-within {
    border-color: #94c840;
    box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.15);
  }
}

.contact-list-scroll__search-icon {
  color: #94a3b8;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.contact-list-scroll__search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.875rem;
  color: #1e293b;
  background: transparent;
  min-width: 0;

  &::placeholder {
    color: #94a3b8;
  }
}

.contact-list-scroll__search-clear {
  color: #94a3b8;
  cursor: pointer;
  font-size: 0.875rem;
  flex-shrink: 0;
  padding: 2px;
  border-radius: 4px;
  transition: color 0.15s ease, background-color 0.15s ease;

  &:hover {
    color: #64748b;
    background-color: #f1f5f9;
  }
}

// ---- Selected contact banner ----
.contact-list-scroll__selected-banner {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #eff6ff;
  border-bottom: 1px solid #bfdbfe;
}

.contact-list-scroll__selected-banner-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.contact-list-scroll__selected-banner-icon {
  color: #64748b;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.contact-list-scroll__selected-banner-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.contact-list-scroll__selected-banner-name {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-list-scroll__selected-banner-email {
  font-size: 0.6875rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-list-scroll__selected-banner-clear {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border: none;
  background: transparent;
  color: #94a3b8;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: #f1f5f9;
    color: #475569;
  }
}

// ---- Segment conditions section ----
.contact-list-scroll__segment-section {
  flex-shrink: 0;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.contact-list-scroll__segment-notice {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  font-size: 0.8125rem;
  color: #64748b;
  line-height: 1.4;
}

.contact-list-scroll__segment-notice-icon {
  font-size: 1rem;
  color: #94c840;
  flex-shrink: 0;
  margin-top: 1px;
}

.contact-list-scroll__segment-notice-text {
  flex: 1;
  text-align: left;
}

.contact-list-scroll__conditions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0 1rem 0.75rem;
}

.contact-list-scroll__condition {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.8125rem;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  &--checked {
    background: rgba(148, 200, 64, 0.08);
    border-color: #94c840;
    color: #475569;

    &:hover {
      background: rgba(148, 200, 64, 0.12);
      border-color: #94c840;
    }
  }
}

.contact-list-scroll__condition-check {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background: #e2e8f0;
  color: #ffffff;
  font-size: 0.625rem;
  transition: all 0.15s ease;

  .contact-list-scroll__condition--checked & {
    background: #94c840;
  }
}

.contact-list-scroll__condition-checkbox {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.contact-list-scroll__condition-label {
  font-weight: 500;
}

.contact-list-scroll__condition-info-wrapper {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.contact-list-scroll__condition-info {
  color: #cbd5e1;
  font-size: 0.875rem;
  transition: color 0.15s ease;
  cursor: pointer;

  &:hover {
    color: #64748b;
  }

  .contact-list-scroll__condition:hover & {
    color: #94a3b8;
  }
}

.contact-list-scroll__tabs {
  display: flex;
  border-top: 1px solid #e2e8f0;
}

.contact-list-scroll__tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.625rem 0.5rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #64748b;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: #475569;
    background: #ffffff;
  }

  &--active {
    color: #94c840;
    background: #ffffff;
    box-shadow: inset 0 -2px 0 #94c840;
  }
}

.contact-list-scroll__tab-count {
  padding: 0.125rem 0.375rem;
  background: #e2e8f0;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;

  .contact-list-scroll__tab--active & {
    background: rgba(148, 200, 64, 0.15);
    color: #94c840;
  }
}

// ---- Scrollable list ----
.contact-list-scroll__list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
}

// ---- Contact row ----
.contact-list-scroll__row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  background-color: #ffffff;
  transition: background-color 0.2s ease;
  position: relative;

  &:hover {
    background-color: #f8fafc;
  }

  &--selected {
    background-color: rgba(148, 200, 64, 0.08);

    &:hover {
      background-color: rgba(148, 200, 64, 0.12);
    }

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background-color: #94c840;
    }
  }

  &:last-child {
    border-bottom: none;
  }
}

.contact-list-scroll__radio {
  flex-shrink: 0;
}

.contact-list-scroll__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.25rem;
  text-align: left; /* Explicitly reset any inherited centered alignment */
}

.contact-list-scroll__name-row {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.contact-list-scroll__name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.25;

  &--empty {
    font-weight: 400;
    font-style: italic;
    color: #94a3b8;
  }
}

.contact-list-scroll__email {
  font-size: 0.8125rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left; /* Force left alignment */
  line-height: 1.25;
}

// ---- Empty state ----
.contact-list-scroll__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #94a3b8;
  gap: 0.75rem;
}

.contact-list-scroll__empty-icon {
  font-size: 2.5rem;
  color: #e2e8f0;
}

// ---- Loading skeletons ----
.contact-list-scroll__loading {
  padding: 0;
}

.contact-list-scroll__skeleton {
  display: flex;
  align-items: center;
  gap: 1rem; // Match row gap
  padding: 0.75rem 1.25rem; // Match row padding
  border-bottom: 1px solid #f1f5f9;
}

.contact-list-scroll__skeleton-radio {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #e2e8f0;
  flex-shrink: 0;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.contact-list-scroll__skeleton-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.contact-list-scroll__skeleton-name {
  height: 14px;
  width: 50%;
  border-radius: 4px;
  background-color: #e2e8f0;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.contact-list-scroll__skeleton-email {
  height: 12px;
  width: 70%;
  border-radius: 4px;
  background-color: #f1f5f9;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
  animation-delay: 0.2s;
}

@keyframes skeleton-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

// ---- Segment conditions banner + tabs ----
.contact-list-scroll__segment-banner {
  flex-shrink: 0;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f8faf5;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.contact-list-scroll__segment-banner-inner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.contact-list-scroll__segment-banner-icon {
  color: #7ab32a;
  font-size: 0.8125rem;
  flex-shrink: 0;
}

.contact-list-scroll__segment-banner-text {
  font-size: 0.75rem;
  font-weight: 500;
  color: #5a8c1a;
  line-height: 1.3;
}

// ---- Filter tabs ----
.contact-list-scroll__tabs {
  display: flex;
  gap: 0.375rem;
  background: #edf0e7;
  border-radius: 8px;
  padding: 3px;
}

.contact-list-scroll__tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    color: #475569;
    background: rgba(255, 255, 255, 0.5);
  }

  &--active {
    color: #5a8c1a;
    background: #ffffff;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

    &:hover {
      background: #ffffff;
      color: #5a8c1a;
    }
  }
}

.contact-list-scroll__tab-count {
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 8px;
  background: rgba(148, 200, 64, 0.12);
  color: #7ab32a;

  .contact-list-scroll__tab:not(.contact-list-scroll__tab--active) & {
    background: #e2e8f0;
    color: #94a3b8;
  }
}

// ---- Match badges ----
.contact-list-scroll__match-badges {
  display: flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  margin-left: auto;
}

.contact-list-scroll__match-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 0.625rem;
  font-weight: 600;
  color: #5a8c1a;
  background: rgba(148, 200, 64, 0.15);
  white-space: nowrap;
  line-height: 1.4;
  cursor: default;
}

// ---- Sentinel / load more ----
.contact-list-scroll__sentinel {
  min-height: 1px;
}

// ---- Loading overlay ----
.contact-list-scroll__overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.contact-list-scroll__overlay-spinner {
  font-size: 1.5rem;
  color: #7ab32a;
}
</style>

<style lang="scss">
.contact-list-scroll__overlay-panel {
  border-radius: 12px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  overflow: hidden;

  .p-overlaypanel-content {
    padding: 0;
  }
}

.contact-list-scroll__popover-content {
  min-width: 320px;
  max-width: 500px;
  min-height: 60px;
  padding: 1.25rem;
  position: relative;
  background-color: #f8fafc;

  // Modernize the nested SegmentationsPanel
  .unit-box {
    border: none;
    padding: 0 !important;
    background: transparent;
  }

  .blockOR-container {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;

    &:hover {
      border-color: #cbd5e1;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05);
    }
  }

  // Center and style connectors (ET / OU)
  .connector {
    height: 2rem !important;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    margin: 4px 0;

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      height: 1px;
      background-color: #e2e8f0;
      z-index: 1;
    }
  }

  .connector-readonly,
  .connector-choose-button {
    position: relative !important;
    transform: none !important;
    left: auto !important;
    top: auto !important;
    background: #f1f5f9;
    color: #475569;
    font-size: 0.7rem;
    font-weight: 700;
    padding: 0.25rem 0.75rem;
    border-radius: 999px;
    border: 1px solid #e2e8f0;
    letter-spacing: 0.05em;
    z-index: 2;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .line {
    display: none !important;
  }

  .p-1, .p-2 {
    padding: 0.75rem !important;
  }

  .field-wrapper {
    margin-bottom: 0 !important;
  }
}
</style>
